import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound, Lock, ShieldCheck, Eye, EyeOff, Landmark } from "lucide-react";

function PwInput({ id, value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input id={id} type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder}
        className="h-11 rounded-xl mt-1 pr-11" data-testid={id} />
      <button type="button" onClick={() => setShow((s) => !s)} aria-label="Toggle visibility"
        className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-muted-foreground hover:text-foreground" data-testid={`${id}-toggle`}>
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function ChangePassword() {
  const [hasWdPw, setHasWdPw] = useState(false);
  const [login, setLogin] = useState({ old_password: "", new_password: "", confirm: "" });
  const [wd, setWd] = useState({ old_password: "", new_password: "", confirm: "" });
  const [busyLogin, setBusyLogin] = useState(false);
  const [busyWd, setBusyWd] = useState(false);
  const [sec, setSec] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/account/security-status").then((r) => { setHasWdPw(!!r.data.has_withdrawal_password); setSec(r.data); })
      .catch(() => setHasWdPw(true));  // safer default: require current fund password
  }, []);

  const changeLogin = async () => {
    if (!login.old_password || !login.new_password) return toast.error("Please fill in both fields.");
    if (login.new_password.length < 6) return toast.error("New login password must be at least 6 characters.");
    if (login.new_password !== login.confirm) return toast.error("New passwords do not match.");
    setBusyLogin(true);
    try {
      await api.post("/account/change-password", { old_password: login.old_password, new_password: login.new_password });
      toast.success("Login password updated successfully.");
      setLogin({ old_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyLogin(false); }
  };

  const changeWd = async () => {
    if (hasWdPw && !wd.old_password) return toast.error("Please enter your current fund password.");
    if (wd.new_password.length < 4) return toast.error("New fund password must be at least 4 characters.");
    if (wd.new_password !== wd.confirm) return toast.error("New fund passwords do not match.");
    setBusyWd(true);
    try {
      await api.post("/account/change-withdrawal-password", { old_password: wd.old_password || undefined, new_password: wd.new_password });
      toast.success("Fund password updated successfully.");
      setWd({ old_password: "", new_password: "", confirm: "" });
      setHasWdPw(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyWd(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="change-password-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold">Security & Passwords</h1>
        <p className="text-sm text-muted-foreground mt-1">Update your login password and your fund (withdrawal) password.</p>
      </div>

      {/* Receiving account */}
      {sec && (
        <div className="bg-card border border-border rounded-3xl p-6" data-testid="security-receiving-account">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`h-11 w-11 rounded-2xl grid place-items-center shrink-0 ${sec.bank_status === "VERIFIED" ? "bg-green-500/15" : "bg-gold/15"}`}>
                <Landmark className={`h-5 w-5 ${sec.bank_status === "VERIFIED" ? "text-green-600" : "text-gold"}`} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="font-heading text-lg font-bold">Receiving Account</h2>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sec.bank_status === "VERIFIED" ? "bg-green-500/15 text-green-600" : "bg-gold/15 text-gold"}`} data-testid="security-account-status">
                    {sec.bank_status === "VERIFIED" ? "Verified ✓" : (sec.bank_status === "REQUIRES_UPDATE" ? "Update Pending" : "Setup Required")}
                  </span>
                </div>
                {sec.bank_account ? (
                  <p className="text-xs text-muted-foreground truncate">{sec.bank_account.bank_name} • {sec.bank_account.account_number_masked} • {sec.bank_account.account_name}</p>
                ) : <p className="text-xs text-muted-foreground">Set up a bank account to receive payouts.</p>}
              </div>
            </div>
            <Button variant="outline" className="rounded-full" onClick={() => nav(sec.bank_account ? "/wallets" : "/wallets?setup=1")} data-testid="manage-account-security">
              {sec.bank_account ? "Manage in Wallets" : "Set Up"}
            </Button>
          </div>
        </div>
      )}

      {/* Login password */}
      <div className="bg-card border border-border rounded-3xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl navy-gradient grid place-items-center shrink-0"><KeyRound className="h-5 w-5 text-white" /></div>
          <div>
            <h2 className="font-heading text-lg font-bold">Login Password</h2>
            <p className="text-xs text-muted-foreground">Used to sign in to your account.</p>
          </div>
        </div>
        <div><Label htmlFor="login-old">Current Password</Label><PwInput id="login-old" value={login.old_password} onChange={(e) => setLogin({ ...login, old_password: e.target.value })} placeholder="Enter current password" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label htmlFor="login-new">New Password</Label><PwInput id="login-new" value={login.new_password} onChange={(e) => setLogin({ ...login, new_password: e.target.value })} placeholder="At least 6 characters" /></div>
          <div><Label htmlFor="login-confirm">Confirm New Password</Label><PwInput id="login-confirm" value={login.confirm} onChange={(e) => setLogin({ ...login, confirm: e.target.value })} placeholder="Re-enter new password" /></div>
        </div>
        <Button className="rounded-full navy-gradient text-white font-semibold" onClick={changeLogin} disabled={busyLogin} data-testid="save-login-password-btn">
          {busyLogin ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Login Password"}
        </Button>
      </div>

      {/* Fund / withdrawal password */}
      <div className="bg-card border border-border rounded-3xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-gold/15 grid place-items-center shrink-0"><Lock className="h-5 w-5 text-gold" /></div>
          <div>
            <h2 className="font-heading text-lg font-bold">Fund Password</h2>
            <p className="text-xs text-muted-foreground">Required to withdraw funds and opt out.</p>
          </div>
        </div>
        {!hasWdPw && (
          <div className="text-sm bg-gold/10 border border-gold/30 rounded-2xl p-3 flex items-start gap-2" data-testid="wd-not-set-note">
            <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-gold" />
            <span>You haven't set a fund password yet. Set one below — no current password needed.</span>
          </div>
        )}
        {hasWdPw && (
          <div><Label htmlFor="wd-old">Current Fund Password</Label><PwInput id="wd-old" value={wd.old_password} onChange={(e) => setWd({ ...wd, old_password: e.target.value })} placeholder="Enter current fund password" /></div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label htmlFor="wd-new">New Fund Password</Label><PwInput id="wd-new" value={wd.new_password} onChange={(e) => setWd({ ...wd, new_password: e.target.value })} placeholder="At least 4 characters" /></div>
          <div><Label htmlFor="wd-confirm">Confirm New Fund Password</Label><PwInput id="wd-confirm" value={wd.confirm} onChange={(e) => setWd({ ...wd, confirm: e.target.value })} placeholder="Re-enter new fund password" /></div>
        </div>
        <Button className="rounded-full navy-gradient text-white font-semibold" onClick={changeWd} disabled={busyWd} data-testid="save-fund-password-btn">
          {busyWd ? <Loader2 className="h-4 w-4 animate-spin" /> : hasWdPw ? "Update Fund Password" : "Set Fund Password"}
        </Button>
      </div>
    </div>
  );
}
